import { useSnapshot } from "valtio";
import { authStore } from "@/store/auth";
import { TLoginResponse, TApiResponse, TErrorMessage, TSuccess, TUser } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { login, resetPassword, forgotPassword, resendOtp, getUser, verifyEmail, register, update_user, changePassword } from "@/features/auth/services/auth-service";
import { TChangePasswordInput } from "@/features/auth/services/auth-service";

import toast from "react-hot-toast";


export function useUser() {
    const { isLoading, isFetching, data } = useQuery({
        queryKey: ["user"],
        queryFn: () => getUser(),
    });

    return { isLoading, isFetching, user: data }
}

export function useLogin(
    onSuccess?: TSuccess<TApiResponse<TLoginResponse>>
) {
    const auth = useSnapshot(authStore);
    
    return useMutation({
        mutationFn: login ,
        onSuccess: (res) => {
            toast.success("login sent successfully");

            auth.setToken(res.results.token);
            auth.setUser(res.results.user);
            
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}


export function useForgotPassword(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    return useMutation({
        mutationFn: forgotPassword,
        onSuccess: (res) => {
            toast.success("otp token sent to provided email, successfully");
            
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

export function useResetPassword(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    return useMutation({
        mutationFn: resetPassword,
        onSuccess: (res) => {
            toast.success("password reset successfully");
            
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

export function useResendOtp(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    return useMutation({
        mutationFn: resendOtp,
        onSuccess: (res) => {
            toast.success("a new otp has been sent to your email");

            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

export function useVerifyEmail(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    return useMutation({
        mutationFn: verifyEmail,
        onSuccess: (res) => {
            toast.success("verification token has been sent to your email account");
            
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

export function useUserRegister(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    
    return useMutation({
        mutationFn: register,
        onSuccess: (res) => {
            toast.success("registration successful");
            
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

export function useUserUpadateUser(
    onSuccess?: TSuccess<TApiResponse<TUser>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ input, id }: { input: FormData, id: number | string }) => (
            update_user(input, id.toString())
        ),
        onSuccess: async (res) => {
            toast.success(res.message ?? "details updated successfully");

            // `useUser` holds the copy every settings tab prefills from, so it has to be
            // refetched before the next tab reads a stale account.
            await queryClient.invalidateQueries({ queryKey: ["user"] });

            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}

export function useChangePassword(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    
    return useMutation({
        mutationFn: (input: TChangePasswordInput) => changePassword(input),
        onSuccess: (res) => {
            toast.success("password changed successfully");
            
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    })
}